import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Badge,
  Box,
  Button,
  type ButtonProps,
  FormControl,
  FormHelperText,
  FormLabel,
  HStack,
  Icon,
  IconButton,
  Input,
  InputGroup,
  InputRightElement,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  SimpleGrid,
  Stack,
  Table,
  Tbody,
  Text,
  Textarea,
  useDisclosure,
} from "@chakra-ui/react";
import {
  type KeyboardEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { FiCheck, FiX, FiXCircle } from "react-icons/fi";
import {
  PublicBidStatus,
  type BuyerPublicBidDetailDto,
  type PublicBidRejectionGroundDto,
  useGetPublicBidRejectionDocumentsQuery,
  useRejectPublicBidMutation,
} from "../../../../autogen/bff-api";
import { DropTarget } from "../../../../common/documents/UppyUploader/DropTarget";
import {
  FileRow,
  isIncompleteFileUpload,
  type FileUpload,
  UploadCompletedStatus,
} from "../../../../common/documents/UppyUploader/FileRow";
import { useUppyUploader } from "../../../../common/documents/UppyUploader/useUppyUploader";
import { useApiError } from "../../../../common/errors/useApiError";
import { TipTap } from "../../../../common/input/TipTap/TipTap";
import { SectionCard } from "../../../sourcing-events/portal/view-single/SectionCard";
import { RejectedSummary } from "./BidRejectionSummary";
import {
  type BidRejectionGroundGroup,
  getApplicableRejectionGroundGroups,
} from "./bidRejectionGrounds";
import {
  REJECTION_COMPLAINT_INFO,
  buildSupplierRejectionMessageMarkdown,
  getGroundMessageMarkdown,
} from "./bidRejectionMessage";

interface Props {
  detail: BuyerPublicBidDetailDto;
  onViewRejectionDetails?: () => void;
}

export const BidRejectionActionCard = ({
  detail,
  onViewRejectionDetails,
}: Props) => {
  if (detail.bid.status === PublicBidStatus.Rejected) {
    return (
      <RejectedSummary detail={detail} onViewDetails={onViewRejectionDetails} />
    );
  }
  if (detail.bid.status !== PublicBidStatus.Submitted) {
    return null;
  }
  return <RejectAction detail={detail} />;
};

const RejectAction = ({ detail }: Props) => {
  const { t } = useTranslation();

  return (
    <SectionCard icon={FiXCircle} title={t("Actions")}>
      <RejectBidButton detail={detail} />
    </SectionCard>
  );
};

export const RejectBidButton = ({
  detail,
  size = "md",
}: {
  detail: BuyerPublicBidDetailDto;
  size?: ButtonProps["size"];
}) => {
  const { t } = useTranslation();
  const disclosure = useDisclosure();

  return (
    <>
      <Button
        size={size}
        colorScheme="red"
        variant="outline"
        width="full"
        onClick={disclosure.onOpen}
      >
        {t("Avvis leverandør")}
      </Button>
      {disclosure.isOpen ? (
        <RejectBidModal
          detail={detail}
          isOpen={disclosure.isOpen}
          onClose={disclosure.onClose}
        />
      ) : null}
    </>
  );
};

const RejectBidModal = ({
  detail,
  isOpen,
  onClose,
}: {
  detail: BuyerPublicBidDetailDto;
  isOpen: boolean;
  onClose: () => void;
}) => {
  const { t } = useTranslation();
  const { triggerSoft } = useApiError();
  const [rejectPublicBid, rejectState] = useRejectPublicBidMutation();
  const titleSupplier = formatSupplierTitle(
    detail.ownerName,
    detail.ownerOrganizationNumber,
  );
  const [selectedKeys, setSelectedKeys] = useState<string[]>([]);
  const [reason, setReason] = useState("");
  const [messageByGroundKey, setMessageByGroundKey] = useState<
    Record<string, string>
  >({});
  const [attachmentIds, setAttachmentIds] = useState<string[]>([]);
  const [areAttachmentsUploading, setAreAttachmentsUploading] = useState(false);
  const [openGroupIndexes, setOpenGroupIndexes] = useState<number[]>([]);
  const [groundSearch, setGroundSearch] = useState("");
  const expansionBeforeSearchRef = useRef<number[] | null>(null);
  const groundGroups = useMemo(
    () => getApplicableRejectionGroundGroups(detail.foaPart),
    [detail.foaPart],
  );

  const selectedGrounds = useMemo(
    () =>
      groundGroups
        .flatMap((group) => group.grounds)
        .filter((ground) => selectedKeys.includes(ground.key)),
    [groundGroups, selectedKeys],
  );
  const messageMarkdown = useMemo(
    () =>
      buildSupplierRejectionMessageMarkdown(
        detail.ownerName,
        selectedGrounds,
        messageByGroundKey,
      ),
    [detail.ownerName, messageByGroundKey, selectedGrounds],
  );
  const canSubmit =
    selectedGrounds.length > 0 &&
    reason.trim().length > 0 &&
    messageMarkdown.length > 0 &&
    !areAttachmentsUploading;

  const onAccordionChange = (nextIndex: number | number[]) => {
    setOpenGroupIndexes(Array.isArray(nextIndex) ? nextIndex : [nextIndex]);
  };

  const handleAttachmentIdsChange = useCallback((nextIds: string[]) => {
    setAttachmentIds(nextIds);
  }, []);

  const handleAttachmentsUploadingChange = useCallback(
    (isUploading: boolean) => {
      setAreAttachmentsUploading(isUploading);
    },
    [],
  );

  const updateGroundSearch = useCallback(
    (nextSearch: string) => {
      const wasSearching = groundSearch.trim().length > 0;
      const isSearching = nextSearch.trim().length > 0;

      if (!wasSearching && isSearching) {
        expansionBeforeSearchRef.current = openGroupIndexes;
        setOpenGroupIndexes(groundGroups.map((_, index) => index));
      }

      if (wasSearching && !isSearching) {
        setOpenGroupIndexes(expansionBeforeSearchRef.current ?? []);
        expansionBeforeSearchRef.current = null;
      }

      setGroundSearch(nextSearch);
    },
    [groundGroups, groundSearch, openGroupIndexes],
  );

  const toggleGround = (ground: PublicBidRejectionGroundDto) => {
    const nextSelectedKeys = selectedKeys.includes(ground.key)
      ? selectedKeys.filter((key) => key !== ground.key)
      : [...selectedKeys, ground.key];

    setSelectedKeys(nextSelectedKeys);
  };

  const updateGroundMessage = (
    ground: PublicBidRejectionGroundDto,
    content: string,
  ) => {
    setMessageByGroundKey((messages) => ({
      ...messages,
      [ground.key]: content,
    }));
  };

  const submit = async () => {
    if (!canSubmit) return;
    try {
      await rejectPublicBid({
        eventId: detail.bid.forEvent,
        bidId: detail.bid.id,
        rejectPublicBidRequestDto: {
          grounds: selectedGrounds,
          reason: reason.trim(),
          message: messageMarkdown,
          attachmentIds,
        },
      }).unwrap();
      onClose();
    } catch (e) {
      triggerSoft(e);
    }
  };

  return (
    <Modal
      isCentered
      isOpen={isOpen}
      onClose={onClose}
      size="6xl"
      scrollBehavior="inside"
    >
      <ModalOverlay />
      <ModalContent
        rounded="2xl"
        overflow="hidden"
        maxH={{ base: "calc(100vh - 2rem)", md: "calc(100vh - 4rem)" }}
        my={{ base: "4", md: "8" }}
        display="flex"
        flexDirection="column"
      >
        <ModalHeader
          px={{ base: "5", md: "8" }}
          py="6"
          borderBottom="1px solid"
          borderColor="red.100"
          bg="red.50"
          color="smText"
          flexShrink={0}
        >
          {t("Avvis {{supplier}}", { supplier: titleSupplier })}
        </ModalHeader>
        <ModalCloseButton top="5" right={{ base: "5", md: "8" }} size="lg" />
        <ModalBody px={{ base: "5", md: "8" }} py="6" flex="1" overflowY="auto">
          <SimpleGrid
            columns={{ base: 1, lg: 2 }}
            spacing="6"
            alignItems="start"
          >
            <Stack spacing="3">
              <Text fontWeight="semibold" color="smText" mb="2">
                {t("Velg lovhjemmel")}
              </Text>
              <InputGroup size="sm">
                <Input
                  aria-label={
                    t("Søk i hjemler eller beskrivelser") ?? undefined
                  }
                  placeholder={t("Søk i hjemler eller beskrivelser") ?? ""}
                  value={groundSearch}
                  onChange={(e) => updateGroundSearch(e.target.value)}
                  pr={groundSearch ? "9" : undefined}
                />
                {groundSearch ? (
                  <InputRightElement>
                    <IconButton
                      aria-label={t("Tøm søk") ?? undefined}
                      icon={<FiX />}
                      size="xs"
                      variant="ghost"
                      onClick={() => updateGroundSearch("")}
                    />
                  </InputRightElement>
                ) : null}
              </InputGroup>
              {groundGroups.length ? (
                <Accordion
                  allowMultiple
                  reduceMotion
                  index={openGroupIndexes}
                  onChange={onAccordionChange}
                >
                  {groundGroups.map((group, index) => (
                    <LegalBasisGroupPanel
                      key={`${group.kind}-${group.description}`}
                      group={group}
                      isOpen={openGroupIndexes.includes(index)}
                      search={groundSearch}
                      selectedKeys={selectedKeys}
                      onToggleGround={toggleGround}
                      messageByGroundKey={messageByGroundKey}
                      onUpdateGroundMessage={updateGroundMessage}
                    />
                  ))}
                </Accordion>
              ) : (
                <Text fontSize="sm" color="smMuted">
                  {t(
                    "Det finnes ingen avvisningshjemler for denne anskaffelsens FOA-del.",
                  )}
                </Text>
              )}
            </Stack>

            <Stack spacing="5">
              <FormControl id="bid-rejection-reason">
                <FormLabel>{t("Begrunnelse")}</FormLabel>
                <Textarea
                  id="bid-rejection-reason"
                  aria-label={t("Begrunnelse") ?? undefined}
                  rows={4}
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder={
                    t(
                      "Beskriv faktum og vurderingen som gjør at leverandøren skal avvises.",
                    ) ?? ""
                  }
                />
              </FormControl>

              <SupplierMessagePreview
                supplierName={detail.ownerName}
                grounds={selectedGrounds}
                messageByGroundKey={messageByGroundKey}
              />

              <RejectionAttachmentsField
                eventId={detail.bid.forEvent}
                bidId={detail.bid.id}
                onAttachmentIdsChange={handleAttachmentIdsChange}
                onUploadingChange={handleAttachmentsUploadingChange}
              />
            </Stack>
          </SimpleGrid>
        </ModalBody>
        <ModalFooter
          px={{ base: "5", md: "8" }}
          py="4"
          borderTop="1px solid"
          borderColor="smBorder"
          bg="smBackground"
          boxShadow="0 -8px 24px rgba(15, 23, 42, 0.05)"
          flexShrink={0}
        >
          <HStack spacing="3">
            <Button variant="ghost" onClick={onClose}>
              {t("Cancel")}
            </Button>
            <Button
              colorScheme="red"
              onClick={submit}
              isDisabled={!canSubmit}
              isLoading={rejectState.isLoading}
            >
              {t("Avvis leverandør")}
            </Button>
          </HStack>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

const LegalBasisGroupPanel = ({
  group,
  isOpen,
  search,
  selectedKeys,
  onToggleGround,
  messageByGroundKey,
  onUpdateGroundMessage,
}: {
  group: BidRejectionGroundGroup;
  isOpen: boolean;
  search: string;
  selectedKeys: string[];
  onToggleGround: (ground: PublicBidRejectionGroundDto) => void;
  messageByGroundKey: Record<string, string>;
  onUpdateGroundMessage: (
    ground: PublicBidRejectionGroundDto,
    content: string,
  ) => void;
}) => {
  const { t } = useTranslation();
  const normalizedSearch = search.trim().toLowerCase();
  const groupTitle =
    group.kind === "mandatory"
      ? t("Pliktige avvisningshjemler")
      : t("Valgfrie avvisningshjemler");
  const selectedGrounds = group.grounds.filter((ground) =>
    selectedKeys.includes(ground.key),
  );
  const visibleGrounds = normalizedSearch
    ? group.grounds.filter((ground) =>
        [ground.label, ground.foaReference]
          .join(" ")
          .toLowerCase()
          .includes(normalizedSearch),
      )
    : group.grounds;

  return (
    <AccordionItem
      border="1px solid"
      borderColor="smBorder"
      rounded="md"
      mb="2"
      overflow="hidden"
    >
      <AccordionButton px="3" py="3" _expanded={{ bg: "gray.50" }}>
        <Box flex="1" textAlign="left" minW="0">
          <HStack spacing="3" justify="space-between" align="flex-start">
            <Box minW="0">
              <Text fontWeight="semibold" color="smText">
                {groupTitle}
              </Text>
              <Text fontSize="sm" color="smMuted" mt="1">
                {group.description}
              </Text>
            </Box>
            <Badge
              variant="subtle"
              colorScheme={selectedGrounds.length ? "teal" : "gray"}
              whiteSpace="nowrap"
            >
              {t("{{count}} valgt", { count: selectedGrounds.length })}
            </Badge>
          </HStack>
        </Box>
        <AccordionIcon ml="2" />
      </AccordionButton>
      <AccordionPanel px="3" pb="3" pt="0">
        {isOpen ? (
          <Stack spacing="2" pt="3">
            {visibleGrounds.length ? (
              visibleGrounds.map((ground) => {
                const isSelected = selectedKeys.includes(ground.key);
                const cardProps = {
                  role: "checkbox",
                  "aria-checked": isSelected,
                  "aria-label": `${ground.label} ${ground.foaReference}`,
                  tabIndex: 0,
                  border: "1px solid",
                  borderColor: isSelected ? "smPrimary" : "smBorder",
                  bg: "smBackground",
                  rounded: "md",
                  px: "3",
                  py: "2.5",
                  cursor: "pointer",
                  _hover: {
                    borderColor: isSelected ? "smPrimary" : "gray.300",
                  },
                  onClick: () => onToggleGround(ground),
                  onKeyDown: (event: KeyboardEvent<HTMLDivElement>) => {
                    if (event.key !== " " && event.key !== "Enter") return;
                    event.preventDefault();
                    onToggleGround(ground);
                  },
                } as const;
                if (isSelected) {
                  return (
                    <Box key={ground.key} {...cardProps}>
                      <SelectedGroundCardContent
                        ground={ground}
                        isSelected
                        message={messageByGroundKey[ground.key] ?? ""}
                        onUpdateMessage={(content) =>
                          onUpdateGroundMessage(ground, content)
                        }
                      />
                    </Box>
                  );
                }

                return (
                  <Box key={ground.key} {...cardProps}>
                    <GroundSummary ground={ground} isSelected={false} />
                  </Box>
                );
              })
            ) : (
              <Text fontSize="sm" color="smMuted">
                {t("Ingen treff")}
              </Text>
            )}
          </Stack>
        ) : null}
      </AccordionPanel>
      {!isOpen && selectedGrounds.length ? (
        <Box
          borderTop="1px solid"
          borderColor="smBorder"
          bg="gray.50"
          px="3"
          py="3"
        >
          <Stack spacing="2">
            {selectedGrounds.map((ground) => (
              <SelectedGroundCard
                key={ground.key}
                ground={ground}
                onRemove={() => onToggleGround(ground)}
                message={messageByGroundKey[ground.key] ?? ""}
                onUpdateMessage={(content) =>
                  onUpdateGroundMessage(ground, content)
                }
              />
            ))}
          </Stack>
        </Box>
      ) : null}
    </AccordionItem>
  );
};

const GroundSummary = ({
  ground,
  isSelected,
}: {
  ground: PublicBidRejectionGroundDto;
  isSelected: boolean;
}) => {
  const { t } = useTranslation();

  return (
    <HStack spacing="3" align="flex-start">
      <Box
        aria-hidden="true"
        alignItems="center"
        bg={isSelected ? "smPrimary" : "smBackground"}
        border="2px solid"
        borderColor={isSelected ? "smPrimary" : "gray.300"}
        boxSize="5"
        color="white"
        display="flex"
        flexShrink={0}
        justifyContent="center"
        mt="0.5"
        rounded="sm"
      >
        {isSelected ? <Icon as={FiCheck} boxSize="3.5" /> : null}
      </Box>
      <Stack spacing="1" flex="1" minW="0">
        <Text as="span" fontSize="sm" fontWeight="semibold" color="smText">
          {ground.label}
        </Text>
        <HStack spacing="2" align="center" flexWrap="wrap">
          <Text as="span" fontSize="sm" color="smPrimary">
            {ground.foaReference}
          </Text>
          <Badge
            colorScheme={ground.mandatory ? "red" : "orange"}
            variant="subtle"
          >
            {ground.mandatory ? t("SKAL") : t("KAN")}
          </Badge>
        </HStack>
      </Stack>
    </HStack>
  );
};

const SelectedGroundCardContent = ({
  ground,
  isSelected,
  message,
  onUpdateMessage,
}: {
  ground: PublicBidRejectionGroundDto;
  isSelected: boolean;
  message: string;
  onUpdateMessage: (content: string) => void;
}) => (
  <>
    <GroundSummary ground={ground} isSelected={isSelected} />
    <GroundMessageEditor
      ground={ground}
      message={message}
      onUpdateMessage={onUpdateMessage}
    />
  </>
);

const GroundMessageEditor = ({
  ground,
  message,
  onUpdateMessage,
}: {
  ground: PublicBidRejectionGroundDto;
  message: string;
  onUpdateMessage: (content: string) => void;
}) => {
  const { t } = useTranslation();

  return (
    <Box
      aria-label={
        t("Melding for {{reference}}", {
          reference: ground.foaReference,
        }) ?? undefined
      }
      mt="4"
      w="full"
      cursor="auto"
      onClick={(event) => event.stopPropagation()}
      onKeyDown={(event) => event.stopPropagation()}
    >
      <TipTap
        content={message}
        debounceWait={0}
        maxHeight="44"
        onUpdate={onUpdateMessage}
        toolbar={{
          headings: false,
          colors: false,
          link: false,
          table: false,
          image: false,
          template: false,
          autofill: false,
          underline: false,
        }}
      />
    </Box>
  );
};

const SelectedGroundCard = ({
  ground,
  onRemove,
  message,
  onUpdateMessage,
}: {
  ground: PublicBidRejectionGroundDto;
  onRemove: () => void;
  message: string;
  onUpdateMessage: (content: string) => void;
}) => {
  const { t } = useTranslation();

  return (
    <Box
      border="1px solid"
      borderColor="smPrimarySoft"
      bg="smBackground"
      rounded="md"
      px="3"
      py="2"
    >
      <HStack spacing="3" align="flex-start" justify="space-between">
        <Box flex="1" minW="0">
          <GroundSummary ground={ground} isSelected />
        </Box>
        <IconButton
          aria-label={
            t("Fjern {{label}}", { label: ground.label }) ?? undefined
          }
          icon={<FiX />}
          size="xs"
          variant="ghost"
          onClick={onRemove}
        />
      </HStack>
      <GroundMessageEditor
        ground={ground}
        message={message}
        onUpdateMessage={onUpdateMessage}
      />
    </Box>
  );
};

const SupplierMessagePreview = ({
  supplierName,
  grounds,
  messageByGroundKey,
}: {
  supplierName: string;
  grounds: PublicBidRejectionGroundDto[];
  messageByGroundKey: Record<string, string>;
}) => {
  const { t } = useTranslation();
  const supplier = supplierName.trim() || "leverandøren";
  const previewGrounds = useMemo(
    () =>
      grounds.map((ground) => ({
        ground,
        message: getGroundMessageMarkdown(messageByGroundKey[ground.key] ?? ""),
      })),
    [grounds, messageByGroundKey],
  );

  return (
    <FormControl id="bid-rejection-message">
      <FormLabel>{t("Melding til leverandør")}</FormLabel>
      <Box
        aria-label={t("Forhåndsvisning av melding") ?? undefined}
        border="1px solid"
        borderColor="smBorder"
        rounded="md"
        bg="smBackground"
        px="3"
        py="3"
        maxH="72"
        overflowY="auto"
      >
        {grounds.length ? (
          <Stack spacing="3">
            <Text fontSize="sm" color="smText">
              {t(
                "Leverandøren {{supplier}} avvises fra konkurransen med hjemmel i følgende bestemmelser:",
                { supplier },
              )}
            </Text>
            {previewGrounds.map(({ ground, message }) => {
              return (
                <Box key={ground.key}>
                  <Text fontSize="sm" fontWeight="semibold" color="smText">
                    {ground.foaReference}
                  </Text>
                  <Text fontSize="sm" color="smMuted">
                    {ground.label}
                  </Text>
                  {message ? (
                    <Text
                      mt="2"
                      fontSize="sm"
                      color="smText"
                      whiteSpace="pre-wrap"
                    >
                      {message}
                    </Text>
                  ) : (
                    <Text
                      mt="2"
                      fontSize="sm"
                      color="smMuted"
                      fontStyle="italic"
                    >
                      {t("Ingen tekst skrevet for denne hjemmelen ennå.")}
                    </Text>
                  )}
                </Box>
              );
            })}
            <Box borderTop="1px solid" borderColor="smBorder" pt="3">
              <Text fontSize="sm" fontWeight="semibold" color="smText">
                {t("Klageadgang")}
              </Text>
              <Text fontSize="sm" color="smText" whiteSpace="pre-wrap">
                {REJECTION_COMPLAINT_INFO}
              </Text>
            </Box>
          </Stack>
        ) : (
          <Text mt="3" fontSize="sm" color="smMuted">
            {t("Velg minst én lovhjemmel for å skrive melding.")}
          </Text>
        )}
      </Box>
      <FormHelperText>
        {t(
          "Forhåndsvisningen oppdateres fra teksten du skriver i de valgte lovhjemlene.",
        )}
      </FormHelperText>
    </FormControl>
  );
};

const RejectionAttachmentsField = ({
  eventId,
  bidId,
  onAttachmentIdsChange,
  onUploadingChange,
}: {
  eventId: string;
  bidId: string;
  onAttachmentIdsChange: (attachmentIds: string[]) => void;
  onUploadingChange: (isUploading: boolean) => void;
}) => {
  const { t } = useTranslation();
  const [modalUploadIds, setModalUploadIds] = useState<string[]>([]);
  const [removedUploadIds, setRemovedUploadIds] = useState<string[]>([]);
  const { data, refetch } = useGetPublicBidRejectionDocumentsQuery({
    eventId,
    bidId,
  });
  const { uppy, allFiles, setDocuments, removeUpload } = useUppyUploader({
    entityType: "PublicBidRejection",
    entityId: bidId,
    eventId,
    reloadEntity: refetch,
  });

  useEffect(() => {
    const handleFileAdded = (file: { id: string }) => {
      setModalUploadIds((ids) =>
        ids.includes(file.id) ? ids : [...ids, file.id],
      );
    };

    uppy.on("file-added", handleFileAdded);
    return () => {
      uppy.off("file-added", handleFileAdded);
    };
  }, [uppy]);

  useEffect(() => {
    setDocuments(
      (data?.documents ?? []).filter(
        (document) =>
          modalUploadIds.includes(document.fromUploadId) &&
          !removedUploadIds.includes(document.fromUploadId),
      ),
    );
  }, [data?.documents, modalUploadIds, removedUploadIds, setDocuments]);

  useEffect(() => {
    onAttachmentIdsChange(
      allFiles.filter(isCompletedUpload).map((file) => file.documentId),
    );
    onUploadingChange(allFiles.some(isIncompleteFileUpload));
  }, [allFiles, onAttachmentIdsChange, onUploadingChange]);

  const handleRemoveUpload = useCallback(
    async (file: FileUpload) => {
      await removeUpload(file);
      setRemovedUploadIds((ids) =>
        ids.includes(file.uploadId) ? ids : [...ids, file.uploadId],
      );
      setModalUploadIds((ids) => ids.filter((id) => id !== file.uploadId));
    },
    [removeUpload],
  );

  return (
    <Box>
      <DropTarget
        targetId={`bid-rejection-attachments-${bidId}`}
        uppy={uppy}
        label={t("Vedlegg (valgfritt)") ?? undefined}
        helperText={t("Last opp dokumenter som skal følge avvisningen") ?? ""}
        paddingBottom="0"
      />
      {allFiles.length ? (
        <Box mt="3">
          <Table variant="simple" size="sm">
            <Tbody>
              {allFiles.map((file) => (
                <FileRow
                  key={file.uploadId}
                  file={file}
                  name={file.name}
                  remove={handleRemoveUpload}
                />
              ))}
            </Tbody>
          </Table>
        </Box>
      ) : null}
    </Box>
  );
};

type CompletedFileUpload = Extract<
  FileUpload,
  { status: UploadCompletedStatus.UploadCompleted }
>;

const isCompletedUpload = (file: FileUpload): file is CompletedFileUpload =>
  file.status === UploadCompletedStatus.UploadCompleted;

const formatSupplierTitle = (
  supplierName: string,
  organizationNumber?: string | null,
) => {
  if (!organizationNumber?.trim()) return supplierName;
  return `${supplierName} ${formatOrganizationNumber(organizationNumber)}`;
};

const formatOrganizationNumber = (orgNumber: string) =>
  orgNumber.replace(/^(\d{3})(\d{3})(\d{3})$/, "$1 $2 $3");
